import { authenticate, checkAuth, completionRequest, fetchModels, signOut, signUp, updateUserData, submitContactForm, getSignupCodesStatus } from '@/utils/api'
import { defaultDashboardTab } from '@/router/dashboardTabs'
import { BrowserCache } from '@/utils/cache'
import { Commit, Dispatch } from 'vuex'
import { ApiModel } from './models'
import { TypedApiModel, ModelType } from '@/types/general'
import { CompletionApiRequest } from '@/types/msq'
import { isApiError, SignInRequest } from '@/types/api'
import type { ProviderConfig } from '@/types/providers'
import { connectedProviderSet } from '@/utils/assistants'
import { nanoid } from 'nanoid'

// --- App Version (from build environment) ---
const appVersion = process.env.PACKAGE_VERSION || 'N/A'

// --- OS Theme Detection ---
const prefersDarkColorScheme = (): boolean =>
  window &&
  window.matchMedia &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const getInitialThemeFromOSOrCache = (cachedTheme?: string): string => {
  // Check if a valid theme preference is already stored from a previous session
  if (cachedTheme && ['light', 'dark', 'synthWave'].includes(cachedTheme)) {
    return cachedTheme;
  }
  // If no stored/valid preference, detect OS preference
  return prefersDarkColorScheme() ? 'dark' : 'light';
};
// --- End OS Theme Detection ---

export interface SnackbarUpdate {
  text: string
  timeout?: number
  color?: string
}

export interface LogEvent {
  id: string
  timestamp: number
  title: string
  content?: Object | string
}

export interface LogEventNoId extends Omit<LogEvent, 'id' | 'timestamp'> {}

export interface PendingChatAgent {
  slug: string
  ownerUsername?: string
  chatId?: string
}

export interface PendingWorkflowRunSelection {
  workflowId: string
  runId?: string
}

export type ConfigRefreshTarget = 'providers' | 'models' | 'agents' | 'workflows' | 'factories'
export type ConfigRefreshTokens = Record<ConfigRefreshTarget, number>

export interface GlobalState {
  username: string
  email: string
  authenticated: boolean
  appVersion: string
  authFailureMessage?: string
  canPublishAgents: boolean
  isAdmin: boolean
  // Signup Codes feature flag (from server)
  signupCodesEnabled: boolean
  allApiModels: {
    models: TypedApiModel[]
    agents: ApiModel[]
  }
  snackbar: SnackbarUpdate
  showOptions: boolean
  showGlobalTooltips: boolean
  selectedTab: string
  showLog: boolean
  logEvents: Array<LogEvent>
  currentTheme: string // Add currentTheme to GlobalState
  showAgentWizard: boolean
  showConfigChat: boolean
  showVideoToAgentDialog: boolean
  pendingChatAgent: PendingChatAgent | null
  pendingWorkflowRun: PendingWorkflowRunSelection | null
  configRefreshTokens: ConfigRefreshTokens
  hideWorkflowChatsInHistory: boolean
  autoRetryFailedChatResponses: boolean
  // Experience level chosen on first login. null = not chosen yet (prompt the user).
  userMode: 'simple' | 'advanced' | null
  // A brand-new account was detected (no mode chosen, no providers at first load): prompt for the
  // mode choice once the first provider connects, until a choice is made. Persisted so a refresh
  // mid-onboarding can't skip the prompt. Cleared by setUserMode.
  modeChoicePending: boolean
  // Whether the one-time "Simple mode exists now" announcement has been shown to this (legacy) user.
  // Persisted so it never reappears once dismissed. Defaults false for existing caches.
  simpleUiAnnouncementSeen: boolean
  // Transient: opener message to seed the Configuration Assistant with the next time it opens.
  configChatSeed: string | null
  // Transient: a message to auto-send in the Chat tab once the pending agent is selected.
  pendingChatMessage: string | null
}

export type CachedGlobalState = Pick<
  GlobalState,
  | 'selectedTab'
  | 'currentTheme'
  | 'hideWorkflowChatsInHistory'
  | 'autoRetryFailedChatResponses'
  | 'userMode'
  | 'modeChoicePending'
  | 'simpleUiAnnouncementSeen'
>

export function toCachedGlobalState(state: Partial<GlobalState>): Partial<CachedGlobalState> {
  const cached: Partial<CachedGlobalState> = {}
  if (state.selectedTab !== undefined) {
    cached.selectedTab = state.selectedTab === 'studio' ? defaultDashboardTab : state.selectedTab
  }
  if (state.currentTheme !== undefined) cached.currentTheme = state.currentTheme
  if (state.hideWorkflowChatsInHistory !== undefined) {
    cached.hideWorkflowChatsInHistory = state.hideWorkflowChatsInHistory
  }
  if (state.autoRetryFailedChatResponses !== undefined) {
    cached.autoRetryFailedChatResponses = state.autoRetryFailedChatResponses
  }
  if (state.userMode !== undefined) cached.userMode = state.userMode
  if (state.modeChoicePending !== undefined) cached.modeChoicePending = state.modeChoicePending
  if (state.simpleUiAnnouncementSeen !== undefined) {
    cached.simpleUiAnnouncementSeen = state.simpleUiAnnouncementSeen
  }
  return cached
}

function persistGlobalState(cache: BrowserCache, state: GlobalState): void {
  cache.add('global', toCachedGlobalState(state))
}

export const global = (cached: Partial<CachedGlobalState>, cache: BrowserCache) => ({
  state: (): GlobalState => ({
    username: '',
    email: '',
    authenticated: false,
    appVersion,
    canPublishAgents: false,
    isAdmin: false,
    signupCodesEnabled: false,
    allApiModels: { models: [], agents: [] },
    snackbar: { text: '', timeout: 0 },
    showOptions: false,
    showGlobalTooltips: true,
    selectedTab: cached.selectedTab ?? 'agents',
    showLog: false,
    logEvents: [],
    currentTheme: getInitialThemeFromOSOrCache(cached.currentTheme), // Initialize currentTheme using detection logic
    showAgentWizard: false,
    showConfigChat: false,
    showVideoToAgentDialog: false,
    pendingChatAgent: null,
    pendingWorkflowRun: null,
    configRefreshTokens: {
      providers: 0,
      models: 0,
      agents: 0,
      workflows: 0,
      factories: 0
    },
    hideWorkflowChatsInHistory: cached.hideWorkflowChatsInHistory ?? true,
    autoRetryFailedChatResponses: cached.autoRetryFailedChatResponses ?? true,
    userMode: cached.userMode ?? null,
    modeChoicePending: cached.modeChoicePending ?? false,
    simpleUiAnnouncementSeen: cached.simpleUiAnnouncementSeen ?? false,
    configChatSeed: null,
    pendingChatMessage: null
  }),
  mutations: {
    setShowAgentWizard(state: GlobalState, show: boolean) {
      state.showAgentWizard = show
    },
    setShowConfigChat(state: GlobalState, show: boolean) {
      state.showConfigChat = show
    },
    setShowVideoToAgentDialog(state: GlobalState, show: boolean) {
      state.showVideoToAgentDialog = show
    },
    setUsername(state: GlobalState, username: string) {
      state.username = username
      state.isAdmin = username.toLowerCase() === 'admin'
    },
    setSelectedTab(state: GlobalState, tab: string) {
      state.selectedTab = tab
      persistGlobalState(cache, state)
    },
    setAuthenticated(state: GlobalState, authenticated: boolean) {
      state.authenticated = authenticated
      if (authenticated) {
        state.authFailureMessage = undefined
      } else {
        // also reset isAdmin on logout
        state.isAdmin = false
      }
    },
    setAuthFailureMessage(state: GlobalState, message: string) {
      state.authFailureMessage = message
    },
    setAllApiModels(state: GlobalState, { models, agents }: { models: TypedApiModel[]; agents: ApiModel[] }) {
      state.allApiModels = { models, agents }
    },
    updateSnackbar(state: GlobalState, update: SnackbarUpdate) {
      state.snackbar = update
    },
    showOptions(state: GlobalState, show: boolean) {
      state.showOptions = show
    },
    setShowGlobalTooltips(state: GlobalState) {
      state.showGlobalTooltips = !state.showGlobalTooltips
    },
    setShowLog(state: GlobalState, show: boolean) {
      state.showLog = show
    },
    addLogEvent(state: GlobalState, event: LogEvent) {
      state.logEvents.push(event)

      // Limit array to 200 items by removing oldest events
      if (state.logEvents.length > 200) {
        // Remove oldest events (those at the beginning of the array)
        state.logEvents = state.logEvents.slice(-200)
      }
    },
    setCurrentTheme(state: GlobalState, themeName: string) {
      // Add setCurrentTheme mutation
      state.currentTheme = themeName
      persistGlobalState(cache, state)
    },
    setCanPublishAgents(state: GlobalState, canPublish: boolean) {
      state.canPublishAgents = canPublish
    },
    setEmail(state: GlobalState, email: string) {
      state.email = email
    },
    setIsAdmin(state: GlobalState, isAdmin: boolean) {
      state.isAdmin = isAdmin
    },
    setSignupCodesEnabled(state: GlobalState, enabled: boolean) {
      state.signupCodesEnabled = enabled
    },
    setPendingChatAgent(state: GlobalState, pending: PendingChatAgent | null) {
      state.pendingChatAgent = pending
    },
    setPendingWorkflowRun(state: GlobalState, pending: PendingWorkflowRunSelection | null) {
      state.pendingWorkflowRun = pending
    },
    bumpConfigRefreshToken(state: GlobalState, target: ConfigRefreshTarget) {
      state.configRefreshTokens[target] += 1
    },
    bumpConfigRefreshTokens(state: GlobalState, targets: ConfigRefreshTarget[]) {
      targets.forEach((target) => {
        state.configRefreshTokens[target] += 1
      })
    },
    setHideWorkflowChatsInHistory(state: GlobalState, value: boolean) {
      state.hideWorkflowChatsInHistory = value
      persistGlobalState(cache, state)
    },
    setAutoRetryFailedChatResponses(state: GlobalState, value: boolean) {
      state.autoRetryFailedChatResponses = value
      persistGlobalState(cache, state)
    },
    setUserMode(state: GlobalState, mode: 'simple' | 'advanced' | null) {
      state.userMode = mode
      // A made choice ends the onboarding mode prompt for good.
      state.modeChoicePending = false
      persistGlobalState(cache, state)
    },
    setModeChoicePending(state: GlobalState, pending: boolean) {
      state.modeChoicePending = pending
      persistGlobalState(cache, state)
    },
    setSimpleUiAnnouncementSeen(state: GlobalState, seen: boolean) {
      state.simpleUiAnnouncementSeen = seen
      persistGlobalState(cache, state)
    },
    setConfigChatSeed(state: GlobalState, seed: string | null) {
      // Transient — intentionally not persisted to cache.
      state.configChatSeed = seed
    },
    setPendingChatMessage(state: GlobalState, message: string | null) {
      // Transient — intentionally not persisted to cache.
      state.pendingChatMessage = message
    }
  },
  actions: {
    toggleAgentWizard({ commit }: { commit: Commit }, show: boolean) {
      commit('setShowAgentWizard', show);
    },
    toggleConfigChat({ commit }: { commit: Commit }, show: boolean) {
      commit('setShowConfigChat', show);
    },
    toggleVideoToAgentDialog({ commit }: { commit: Commit }, show: boolean) {
      commit('setShowVideoToAgentDialog', show)
    },
    setUsername({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, username: string) {
      commit('setUsername', username)
      dispatch('addLogEvent', { title: 'Username Set', content: username })
    },
    setTab({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, tab: string) {
      commit('setSelectedTab', tab)
      dispatch('addLogEvent', { title: 'Tab Changed', content: tab })
    },
    setPendingChatAgent({ commit }: { commit: Commit }, pending: PendingChatAgent | null) {
      commit('setPendingChatAgent', pending)
    },
    clearPendingChatAgent({ commit }: { commit: Commit }) {
      commit('setPendingChatAgent', null)
    },
    setPendingWorkflowRun({ commit }: { commit: Commit }, pending: PendingWorkflowRunSelection | null) {
      commit('setPendingWorkflowRun', pending)
    },
    clearPendingWorkflowRun({ commit }: { commit: Commit }) {
      commit('setPendingWorkflowRun', null)
    },
    markConfigResourceDirty({ commit }: { commit: Commit }, target: ConfigRefreshTarget) {
      commit('bumpConfigRefreshToken', target)
    },
    markConfigResourcesDirty({ commit }: { commit: Commit }, targets: ConfigRefreshTarget[]) {
      commit('bumpConfigRefreshTokens', targets)
    },
    toggleHideWorkflowChatsInHistory({ commit }: { commit: Commit }, value: boolean) {
      commit('setHideWorkflowChatsInHistory', value)
    },
    setUserMode({ commit }: { commit: Commit }, mode: 'simple' | 'advanced' | null) {
      commit('setUserMode', mode)
    },
    setModeChoicePending({ commit }: { commit: Commit }, pending: boolean) {
      commit('setModeChoicePending', pending)
    },
    setSimpleUiAnnouncementSeen({ commit }: { commit: Commit }, seen: boolean) {
      commit('setSimpleUiAnnouncementSeen', seen)
    },
    setConfigChatSeed({ commit }: { commit: Commit }, seed: string | null) {
      commit('setConfigChatSeed', seed)
    },
    clearConfigChatSeed({ commit }: { commit: Commit }) {
      commit('setConfigChatSeed', null)
    },
    setPendingChatMessage({ commit }: { commit: Commit }, message: string | null) {
      commit('setPendingChatMessage', message)
    },
    clearPendingChatMessage({ commit }: { commit: Commit }) {
      commit('setPendingChatMessage', null)
    },
    toggleAutoRetryFailedChatResponses({ commit }: { commit: Commit }, value: boolean) {
      commit('setAutoRetryFailedChatResponses', value)
    },
    async fetchSignupCodesStatus({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      const response = await getSignupCodesStatus()
      if (isApiError(response)) {
        commit('updateSnackbar', { text: `Signup Codes Status Error: ${response.message}` })
        dispatch('addLogEvent', { title: 'Signup Codes Status Error', content: response })
        return
      }
      commit('setSignupCodesEnabled', !!response.enabled)
      dispatch('addLogEvent', { title: 'Signup Codes Status', content: { enabled: response.enabled } })
    },

    async authenticate(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      { username, password }: SignInRequest
    ) {
      const response = await authenticate(username, password)

      if (isApiError(response)) {
        console.error('Authentication request failed.')
        commit('setAuthFailureMessage', response.message)
        commit('addLogEvent', { title: 'Authentication Error', content: response })
        return
      }

      const { success, message, canPublishAgents, email, admin } = response
      if (success) {
        console.log('authenticated!')
        commit('setUsername', username)
        commit('setEmail', email || '')
        commit('setAuthenticated', true)
        commit('setCanPublishAgents', canPublishAgents || false)
        commit('setIsAdmin', admin ?? (username.toLowerCase() === 'admin'))
        await dispatch('subscription/refresh', null, { root: true })
        dispatch('addLogEvent', { title: 'Authentication Successful', content: { username } })
      } else {
        console.warn('failed to authenticate.')
        commit('setAuthenticated', false)
        commit('setCanPublishAgents', false)
        commit('setAuthFailureMessage', message)
        await dispatch('subscription/clear', null, { root: true })
        dispatch('addLogEvent', { title: 'Authentication Failed', content: message })
      }
    },

    async checkAuth({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      const response = await checkAuth()

      if (isApiError(response)) {
        console.error('Auth check request failed.')
        commit('setAuthFailureMessage', response.message)
        commit('addLogEvent', { title: 'Auth Check Error', content: response })
        return
      }

      const { success, message, username, canPublishAgents, email, admin } = response
      if (success && username != null) {
        console.log('authenticated!')
        commit('setUsername', username)
        commit('setEmail', email || '')
        commit('setAuthenticated', true)
        commit('setCanPublishAgents', canPublishAgents || false)
        commit('setIsAdmin', admin ?? (username.toLowerCase() === 'admin'))
        await dispatch('subscription/refresh', null, { root: true })
        dispatch('addLogEvent', { title: 'Auth Check Successful', content: { username } })
      } else {
        console.warn('failed to authenticate.')
        commit('setAuthenticated', false)
        commit('setCanPublishAgents', false)
        commit('setAuthFailureMessage', message)
        await dispatch('subscription/clear', null, { root: true })
        dispatch('addLogEvent', { title: 'Auth Check Failed', content: message })
      }
    },
    async sendTelemetry({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, data: any) {
      console.log(`telemetry`)
      console.log(data)
      // TODO
    },
    async updateSnackbar({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, update: SnackbarUpdate) {
      commit('updateSnackbar', update)
      // await track({ type: 'snackbar', text: update.text })
    },
    async toggleLog({ commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: GlobalState }) {
      commit('setShowLog', !state.showLog)
    },
    async addLogEvent({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, event: LogEventNoId) {
      const withId: LogEvent = {
        id: nanoid(),
        timestamp: Date.now(),
        ...event
      }
      commit('addLogEvent', withId)
    },
    async toggleOptions({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, show: boolean) {
      commit('showOptions', show)
      dispatch('addLogEvent', { title: 'Options Visibility Changed', content: show ? 'Shown' : 'Hidden' })
    },
    toggleGlobalTooltips({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      commit('setShowGlobalTooltips')
      dispatch('addLogEvent', { title: 'Global Tooltips Toggled', content: 'Toggled' })
    },
    async signOut({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      const response = await signOut()

      if (isApiError(response)) {
        commit('updateSnackbar', { text: response.message })
        commit('addLogEvent', { title: 'Sign Out Error', content: response })
        return
      }

      commit('setAuthenticated', false)
      await dispatch('subscription/clear', null, { root: true })
      dispatch('addLogEvent', { title: 'User Signed Out', content: 'Success' })
    },
    setTheme({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, themeName: string) {
      // Add setTheme action
      commit('setCurrentTheme', themeName)
      dispatch('addLogEvent', { title: 'Theme Changed', content: themeName })
    },

    async register(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      {
        username,
        first,
        last,
        password,
        email,
        signupCode
      }: { username: string; first: string; last: string; password: string; email: string; signupCode?: string }
    ) {
      const signUpResponse = await signUp({ username, first, last, password, email, signupCode })

      // signUp already handles ApiErrorResponse internally
      const { success, message } = signUpResponse

      if (success) {
        commit('setAuthenticated', { username, authenticated: false })
        const authResponse = await authenticate(username, password)

        if (isApiError(authResponse)) {
          commit('updateSnackbar', { text: `Registration Auth Error: ${authResponse.message}` })
          commit('addLogEvent', { title: 'Registration Auth Error', content: authResponse })
          return { success: false, message: authResponse.message }
        }

        const { success: authSuccess, message: authMessage } = authResponse
        if (authSuccess) {
          return { success: authSuccess, message: authMessage ?? 'Success!' }
        }
      }

      if (!success) {
        commit('updateSnackbar', { text: message })
      }

      return { success, message }
    },

    async submitContactForm(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      contactData: { name: string; email: string; subject: string; message: string }
    ) {
      const response = await submitContactForm(contactData)

      if (isApiError(response)) {
        commit('updateSnackbar', { text: `Contact Form Error: ${response.message}`, timeout: 5000 })
        dispatch('addLogEvent', { title: 'Contact Form Error', content: response })
        return { success: false, message: response.message }
      }

      dispatch('addLogEvent', { title: 'Contact Form Submitted', content: contactData })
      return response
    },

    async fetchModels({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      const response = await fetchModels()

      if (isApiError(response)) {
        commit('updateSnackbar', { text: `Fetch Models Error: ${response.message}` })
        commit('addLogEvent', { title: 'Fetch Models Error', content: response })
        return
      }

      const list = response.data ?? []
      const models: TypedApiModel[] = []
      const agents: ApiModel[] = []

      list.forEach((item: ApiModel) => {
        if (item.object === 'model') {
          let type: ModelType = 'language'
          if (item.id.includes('embed')) {
            type = 'embedding'
          }
          models.push({ ...item, type })
        } else if (item.object === 'agent') {
          agents.push(item)
        }
      })

      commit('setAllApiModels', { models, agents })
      dispatch('addLogEvent', {
        title: 'Models Fetched',
        content: { modelCount: models.length, agentCount: agents.length }
      })
    },

    async completionRequest(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      params: CompletionApiRequest
    ) {
      const response = await completionRequest(params)

      if (isApiError(response)) {
        commit('updateSnackbar', { text: `Completion Request Error: ${response.message}` })
        commit('addLogEvent', { title: 'Completion Request Error', content: response })
        return { error: true, message: response.message }
      }

      dispatch('addLogEvent', { title: 'Completion Request Successful', content: { model: params.modelConfig } })
      return response
    },

    async updateSecret({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, { secretName, secretValue }: { secretName: string; secretValue: string }) {
      const response = await updateUserData('update', secretName, secretValue)

      if (isApiError(response)) {
        commit('updateSnackbar', { text: `${secretName} Update Error: ${response.message}` })
        commit('addLogEvent', { title: `${secretName} Update Error`, content: response })
        return false
      }

      dispatch('addLogEvent', { title: `${secretName} Updated`, content: 'Success' })
      return response
    }
  },
  getters: {
    username: (state: GlobalState) => state.username,
    email: (state: GlobalState) => state.email,
    authenticated: (state: GlobalState) => state.authenticated,
    isAdmin: (state: GlobalState) => state.isAdmin,
    authFailureMessage: (state: GlobalState) => state.authFailureMessage,
    appVersion: (state: GlobalState) => state.appVersion,
    showOptions: (state: GlobalState) => state.showOptions,
    showGlobalTooltips: (state: GlobalState) => state.showGlobalTooltips,
    showAgentWizard: (state: GlobalState) => state.showAgentWizard,
    showConfigChat: (state: GlobalState) => state.showConfigChat,
    showVideoToAgentDialog: (state: GlobalState) => state.showVideoToAgentDialog,
    snackbar: (state: GlobalState) => state.snackbar,
    showLog: (state: GlobalState) => state.showLog,
    logEvents: (state: GlobalState) => state.logEvents,
    selectedTab: (state: GlobalState) => state.selectedTab,
    currentTheme: (state: GlobalState) => state.currentTheme, // Add currentTheme getter
    canPublishAgents: (state: GlobalState) => state.canPublishAgents,
    signupCodesEnabled: (state: GlobalState) => state.signupCodesEnabled,
    pendingChatAgent: (state: GlobalState) => state.pendingChatAgent,
    pendingWorkflowRun: (state: GlobalState) => state.pendingWorkflowRun,
    hideWorkflowChatsInHistory: (state: GlobalState) => state.hideWorkflowChatsInHistory,
    autoRetryFailedChatResponses: (state: GlobalState) => state.autoRetryFailedChatResponses,
    userMode: (state: GlobalState) => state.userMode,
    modeChoicePending: (state: GlobalState) => state.modeChoicePending,
    // True while onboarding is parked on the user-mode choice: a brand-new account (modeChoicePending)
    // whose first provider is now connected but who hasn't picked simple/advanced yet. UserModeDialog
    // shows on this; the config-chat dialog yields the screen while it's true.
    awaitingModeChoice: (state: GlobalState, _getters: unknown, _rootState: unknown, rootGetters: { providerConfigsList?: ProviderConfig[] }): boolean =>
      state.userMode == null &&
      state.modeChoicePending &&
      connectedProviderSet(rootGetters.providerConfigsList || []).size > 0,
    simpleUiAnnouncementSeen: (state: GlobalState) => state.simpleUiAnnouncementSeen,
    // Eligibility for the one-time "Simple mode exists now" announcement. Targets legacy/advanced users
    // (userMode null behaves as advanced) with more than 2 owned agents — new users top out at 2
    // (the config + title utility agents). `!modeChoicePending` keeps it out of the new-user onboarding
    // flow entirely (and so can never stack with UserModeDialog, which needs modeChoicePending). Already
    // in Simple mode → nothing to announce. DashboardView snapshots this once at load so growing past 2
    // agents mid-session can't pop it.
    shouldAnnounceSimpleUi: (state: GlobalState, _getters: unknown, _rootState: unknown, rootGetters: { agentConfigsList?: unknown[] }): boolean =>
      !state.simpleUiAnnouncementSeen &&
      !state.modeChoicePending &&
      state.userMode !== 'simple' &&
      (rootGetters.agentConfigsList?.length ?? 0) > 2,
    configChatSeed: (state: GlobalState) => state.configChatSeed,
    pendingChatMessage: (state: GlobalState) => state.pendingChatMessage,
    configRefreshToken:
      (state: GlobalState) =>
      (target: ConfigRefreshTarget): number =>
        state.configRefreshTokens[target],
    // models
    apiModelsList: (state: GlobalState) => state.allApiModels.models,
    // agents
    apiAgentsList: (state: GlobalState) => state.allApiModels.agents,
    // embedding models
    // embeddingModels: (state: GlobalState) =>
    //   state.allApiModels.models.filter((model: TypedApiModel) => model.type === 'embedding')
  }
})
